import React from 'react';
import { Select, Button } from 'antd';
import { FiRefreshCw, FiFileText } from 'react-icons/fi';
import { InPageSpinner } from '@components/spinners/in-page-spinner';
import { TooltipTop } from '@components/tooltips';
import { RUNS_CHART_LIMIT_OPTIONS, TIME_RANGE_OPTIONS } from '../../constants';
import { formatDateTime } from '../../utils';
import { ClearTaskDialog } from '../clear-task-dialog';
import { StatCard } from './stat-card';
import { RunsDurationChart } from './runs-duration-chart';
import { useOverviewTab } from './use-overview-tab';
import styles from '../../styles.module.scss';

const { Option } = Select;

type OverviewTabProps = {
    dagId: string;
    refreshKey: number;
};

/*
 * DAG detail's Overview tab — failed-run/failed-task stat cards, run-duration chart, and a
 * recent-failed-tasks list with a Clear Task action
 */
export function OverviewTab({ dagId, refreshKey }: OverviewTabProps) {
    const {
        timeRange,
        setTimeRange,
        runsChartLimit,
        setRunsChartLimit,
        failedRunsTotal,
        failedTasksTotal,
        failedRunsSeries,
        failedTasksSeries,
        recentFailedTasks,
        chartRuns,
        isLoading,
        clearDialogTask,
        setClearDialogTask,
        handleViewFullLogs,
        handleRunClick,
        handleTaskCleared,
    } = useOverviewTab(dagId, refreshKey);

    if (isLoading) return <InPageSpinner size="Small" />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Select value={timeRange} onChange={setTimeRange} style={{ width: 160 }}>
                    {TIME_RANGE_OPTIONS.map((option) => (
                        <Option key={option.value} value={option.value}>
                            {option.label}
                        </Option>
                    ))}
                </Select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <StatCard title="Failed Tasks" total={failedTasksTotal} series={failedTasksSeries} color="#ff4d4f" />
                <StatCard title="Failed Runs" total={failedRunsTotal} series={failedRunsSeries} color="#ff4d4f" />
            </div>
            <div className={styles.card} style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 0.25 }}>{`Last ${runsChartLimit} DAG Runs`}</h3>
                    <Select value={runsChartLimit} onChange={setRunsChartLimit} style={{ width: 100 }}>
                        {RUNS_CHART_LIMIT_OPTIONS.map((option) => (
                            <Option key={option} value={option}>
                                {option}
                            </Option>
                        ))}
                    </Select>
                </div>
                <RunsDurationChart runs={chartRuns} onRunClick={handleRunClick} />
            </div>
            <div className={styles.card}>
                <h3 style={{ margin: '0 0 8px', color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 0.25 }}>Recent Failed Tasks</h3>
                {recentFailedTasks.length === 0 && <div style={{ color: '#666a8d' }}>No failed tasks in this range.</div>}
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {recentFailedTasks.map((task) => (
                        <li
                            key={`${task.dag_run_id}-${task.task_id}`}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #2a2c42' }}
                        >
                            <div>
                                <div style={{ color: '#e1e1e6' }}>
                                    {task.task_id} <span style={{ color: '#666a8d', fontSize: 12 }}>{`try ${task.try_number ?? 1}`}</span>
                                </div>
                                <div style={{ color: '#666a8d', fontSize: 12 }}>
                                    {task.dag_run_id} — {formatDateTime(task.start_date)}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 12 }}>
                                <TooltipTop title="Clear Task Instance">
                                    <span className={styles.actionIcon} onClick={() => setClearDialogTask(task)}>
                                        <FiRefreshCw />
                                    </span>
                                </TooltipTop>
                                <Button size="small" onClick={() => handleViewFullLogs(task)}>
                                    <FiFileText style={{ marginRight: 6 }} />
                                    View Full Logs
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
            {clearDialogTask && (
                <ClearTaskDialog
                    dagId={dagId}
                    task={clearDialogTask}
                    onClose={() => setClearDialogTask(null)}
                    onCleared={handleTaskCleared}
                />
            )}
        </div>
    );
}
